<template>
    <KeywordDefinition
        v-if="
            showKeywordDefinition
                && visibleKeywordDefinition
                && question
                && questionContainer
                && keywordDefinitionParams
        "
        :keywordDOMElement="keywordDefinitionParams.keywordDOMElement"
        :keywordClickLocation="keywordDefinitionParams.keywordClickLocation"
        :keyword="visibleKeywordDefinition.keyword"
        :definition="visibleKeywordDefinition.definition"
        :questionContainer="questionContainer"
        :serial="question.serial"
        :keywordLocation="visibleKeywordDefinition.location"
        @close="showKeywordDefinition = false"
    />
    <div class="qotd__container">
        <BonusQuestionSurvey
            v-if="showSurvey"
            :variant="bonusQuestionVariantValue === 'short-answer-question'
                ? 'shortAnswerQuestion'
                : 'explanationAnswerQuestion'
            "
            :bonusQText="bonusQuestionUserText"
            @submit="submitSurvey"
            @close="closeSurvey"
        />
        <QuizContainer
            ref="quizContainer"
            class="qotd"
            :is-dark-mode="isDarkMode"
        >
            <template #header>
                <div class="qotd__exam-info">
                    <ExamMenuCard
                        v-if="currentExam && bundle"
                        :exam="currentExam"
                        :bundle-id="bundle.objectId"
                        theme="silver"
                        :is-clickable="false"
                        :is-dark-mode="isDarkMode"
                    />
                </div>
                <PocketButton
                    class="qotd__quit-btn"
                    type="secondary-yellow-dark"
                    :is-dark-mode="isDarkMode"
                    @click="quitQuiz"
                >
                    Close
                </PocketButton>
            </template>
            <template #question>
                <BonusQuestionExperiment
                    v-if="currentExam && showBonusQuestion && bonusQuestion"
                    :examGuid="currentExam.examGuid"
                    :question="bonusQuestion"
                    :is-dark-mode="isDarkMode"
                    @skip="skipBonusQuestion"
                    @userAnswer="updateUserShortAnswer"
                    @showSurvey="showSurvey = true"
                />
                <Question
                    v-else-if="question && currentExam && !showBonusQuestion && !isLoading"
                    :key="`${question.objectId}-${allowKeyboardShortcuts}-${JSON.stringify(answer)}`"
                    class="qotd__question"
                    ref="qotdContainer"
                    :class="{ 'qotd__question--correct': answer && isCorrect }"
                    :container-el="quizContainerEl"
                    :show-next-question="false"
                    :question="qotdModule.getters.getRenderableQotDQuestion(question)"
                    :quiz-mode="'qotd'"
                    :is-dark-mode="isDarkMode"
                    :previous-choices="previousChoicesTyped"
                    :previous-matrix-choices="previousMatrixChoicesTyped"
                    :previous-build-list-choices="previousBuildListChoicesTyped"
                    :previous-multi-part-multiple-choice-choices="previousChoicesTyped"
                    :global-metrics="showGlobalMetrics ? globalMetrics : null"
                    :show-check-answer="true"
                    :image-url-prefix="`${s3Url || ''}/${question.compositeKey.toUpperCase()}/`"
                    :initial-show-answers="!!answer"
                    :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                    :show-close-button="true"
                    :show-paywall="showPaywall"
                    :hide-references="currentExam.hideReferences"
                    :keyword-definitions="keywordDefinitions"
                    @upgrade="openUpgradeModal"
                    @keywordClick="keywordClicked"
                    @close="quitQuiz"
                    @checkAnswer="submitQotD"
                    @selectedChoices="selectChoices"
                    @update:showExplanation="explanationViewToggled"
                >
                    <template #promptExperiment>
                        <BonusQuestionBanner
                            v-if="showUserExplanationField"
                            class="qotd__bonus-question-banner"
                            variant="explanation-answer-question"
                            :is-dark-mode="isDarkMode"
                        />
                    </template>
                    <template #actionExperiment>
                        <BonusQuestionTextarea
                            v-if="showUserExplanationField"
                            class="qotd__desktop-bonus-question-explanation-textarea"
                            v-model="userExplanation"
                            label="Why is this the correct answer?"
                            height="134px"
                            :disabled="!selectedChoices.length || !!answer"
                            :is-dark-mode="isDarkMode"
                        />
                    </template>
                </Question>
            </template>
            <template #footer>
                <div class="qotd__footer">
                    <div class="qotd__footer-left">
                        <div
                            class="qotd__global-metrics-toggle"
                            :class="{
                                'qotd__global-metrics-toggle--active': showGlobalMetrics,
                                'qotd__global-metrics-toggle--disabled': !answer,
                                'qotd__global-metrics-toggle--enabled': !!answer
                            }"
                        >
                            <GlobalMetricsToggle
                                :show-global-metrics="showGlobalMetrics"
                                :disabled="!answer || showPaywall"
                                :is-dark-mode="isDarkMode"
                                :tooltip-theme="
                                    (
                                        breakpoint === 'polar-bear'
                                        || breakpoint === 'grizzly-bear'
                                        || breakpoint === 'brown-bear'
                                    )
                                        ? 'rightalign' : undefined
                                "
                                @toggleGlobalMetrics="toggleGlobalMetrics"
                            />
                        </div>
                        <QotDShareLink
                            v-if="isTodaysQotD && bundle && currentExam"
                            :bundle="bundle"
                            class="qotd__share--desktop"
                            :exam-metadata="currentExam"
                            :is-dark-mode="isDarkMode"
                        />
                    </div>
                    <div class="qotd__controls">
                        <FlagToggle
                            :key="`flagToggle_${allowKeyboardShortcuts}`"
                            class="qotd__flag-toggle"
                            :is-flagged="isFlagged"
                            :is-dark-mode="isDarkMode"
                            :enable-flag-tooltip="breakpoint !== 'brown-bear' && breakpoint !== 'black-bear'"
                            :enable-flag-keyboard-shortcut="allowKeyboardShortcuts"
                            @toggleFlag="toggleFlag"
                        />
                    </div>
                    <QotDShareLink
                        v-if="isTodaysQotD && bundle && currentExam"
                        :bundle="bundle"
                        class="qotd__share--mobile"
                        :is-dark-mode="isDarkMode"
                        :exam-metadata="currentExam"
                    />
                    <KeyboardShortcutsButton
                        class="qotd__keyboard-shortcuts-btn"
                        :is-dark-mode="isDarkMode"
                        :tooltip-theme="(breakpoint === 'polar-bear' || breakpoint === 'grizzly-bear'
                            || breakpoint === 'brown-bear') ? 'leftalign' : undefined"
                    >
                        <template
                            #keyboardShortcutsModal="{
                                showKeyboardShortcutsModal,
                                closeModalFunc
                            }"
                        >
                            <Portal to="modal">
                                <KeyboardShortcutsModal
                                    v-if="showKeyboardShortcutsModal"
                                    key="qotdKeyboardShortcutsModal"
                                    :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                    :is-dark-mode="isDarkMode"
                                    aria-live="polite"
                                    @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                    @close="closeModalFunc"
                                />
                            </Portal>
                        </template>
                    </KeyboardShortcutsButton>
                </div>
            </template>
        </QuizContainer>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { qotdModule } from '@/store/qotd/module'
import { userModule } from '@/store/user/module'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import UIKit from '@pocketprep/ui-kit'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { quizModule } from '@/store/quiz/module'
import QotDShareLink from '@/components/Quiz/QotDShareLink.vue'
import { screenModule } from '@/store/screen/module'
import { subscriptionModule } from '@/store/subscription/module'
import KeywordDefinition from '@/components/KeywordDefinitions/KeywordDefinition.vue'
/* Start Bonus Question Experiment imports */
import { analyticsModule } from '@/store/analytics/module'
import BonusQuestionExperiment from '@/components/BonusQuestionExperiment/BonusQuestionExperiment.vue'
import BonusQuestionBanner from '@/components/BonusQuestionExperiment/BonusQuestionBanner.vue'
import BonusQuestionSurvey from '@/components/BonusQuestionExperiment/BonusQuestionSurvey.vue'
import BonusQuestionTextarea from '@/components/BonusQuestionExperiment/BonusQuestionTextarea.vue'
import { toastModule } from '@/store/toast/module'
import { keywordsModule } from '@/store/keywords/module'
import { upgradeModule } from '@/store/upgrade/module'
/* End Bonus Question Experiment imports */

// UIKit Question component ChoiceKey props
type TChoiceKey = `${'a' | 'd'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`
type TMatrixChoiceKey = `${'a' | 'd'}${1 | 2 | 3}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`
type TBuildListChoiceKey = `a${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`

const {
    FlagToggle,
    KeyboardShortcutsButton,
    ExamMenuCard,
    Button: PocketButton,
    GlobalMetricsToggle,
    QuizContainer,
    Question,
    KeyboardShortcutsModal,
} = UIKit

const route = useRoute()
const router = useRouter()

const quizContainer = ref<InstanceType<typeof UIKit.QuizContainer> | null>(null)
const qotdContainer = ref<InstanceType<typeof UIKit.Question> | null>(null)

const isLoading = ref(true)
const isSubmitLoading = ref(false)
const showGlobalMetrics = ref(false)
const globalMetrics = ref<Study.Class.GlobalQuestionMetricJSON | null>(null)
const currentExam = ref<Study.Class.ExamMetadataJSON | ClientExam | null>(null)
const isFlagged = ref(false)
const isTogglingFlag = ref(false)
const isCorrect = ref(false)
const isCommunityStatsDisabled = ref(true)
/* Start Bonus Question Experiment properties */
const disableExperiment = ref(false)
const showSurvey = ref(false)
const selectedChoices = ref<Study.Cloud.TChoiceKey[]>([])
const bonusQuestionVariantValue = ref<'short-answer-question' | 'explanation-answer-question' | null>(null)
const userShortAnswer = ref('')
const userExplanation = ref('')
/* End Bonus Question Experiment properties */
const keywordDOMElement = ref<HTMLElement | null>(null)
const showKeywordDefinition = ref(false)
const visibleKeywordDefinition = ref<{
    keyword: string
    definition: string
    location: string
} | null>(null)
const keywordClickLocation = ref<{ x: number; y: number } | null>(null)

const keywordDefinitionParams = computed(() => {
    const el = keywordDOMElement.value
    const loc = keywordClickLocation.value

    if (!el || !loc) return null
    return {
        keywordDOMElement: el,
        keywordClickLocation: loc,
    }
})

const hasActiveSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const quizId = computed(() => {
    return route.params['quizId'] as string | undefined
})

const question = computed(() => {
    if (quizId.value) {
        const quiz = quizModule.getters.getQuizById(quizId.value)

        if (quiz) {
            const answer = quiz.answers[0]

            return quizModule.getters.getAnsweredQuestions().find(q => q.serial === answer?.questionSerial)
        }
    }

    return qotdModule.getters.getQotDQuestion()
})

const isTodaysQotD = computed(() => {
    return qotdModule.getters.getQotDQuestion()?.serial === question.value?.serial
})

const showPaywall = computed(() => {
    return !hasActiveSubscription.value && !isTodaysQotD.value
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const questionContainer = computed(() => {
    const refVal = qotdContainer.value
    return refVal && '$el' in refVal ? refVal.$el as HTMLElement : null
})

const quizContainerEl = computed(() => {
    const refVal = quizContainer.value
    return refVal && '$el' in refVal ? refVal.$el : null
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const flaggedQuestions = computed(() => {
    return userExamMetadataModule.getters.getFlaggedQuestions()
})

const quizSettings = computed(() => {
    return userModule.state.user?.quizSettings || null
})

const allowKeyboardShortcuts = computed(() => {
    return !!quizSettings.value?.enableKeyboardShortcuts
})

const answer = computed(() => {
    return quizId.value
        ? quizModule.getters.getQuizById(quizId.value)?.answers[0] || null
        : qotdModule.getters.getQotDQuiz()?.answers[0] || null
})

const previousChoices = computed(() => {
    return answer.value?.selectedChoices || []
})

// Question component expects literal union types; cast from string[] for type compatibility
const previousChoicesTyped = computed(() => {
    // non matrix and build list questions
    return previousChoices.value as TChoiceKey[]
})

const previousMatrixChoicesTyped = computed(() => {
    return previousChoices.value as TMatrixChoiceKey[]
})

const previousBuildListChoicesTyped = computed(() => {
    return previousChoices.value as TBuildListChoiceKey[]
})

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()
})

const fromName = computed(() => {
    return (route.meta.fromName as string | undefined) || null
})

const bundle = computed(() => {
    return bundleModule.getters.getBundles()
        .find(b => b.exams.find(e => {
            const bundleExam = examMetadataById.value[e.objectId]
            return bundleExam?.examGuid === currentExam.value?.examGuid
        }))
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const keywordDefinitions = computed(() => {
    const questionSerial = question.value?.serial
    const questionKeywords = questionSerial && keywordsModule.getters.getKeywordDefinitions().get(questionSerial)

    if (
        !answer.value
        || !questionKeywords
    ) {
        return []
    }

    return Array.from(questionKeywords, ([ keyword, definition ]) => ({
        keyword,
        definition,
    }))
})

/* Start Bonus Question Experiment Getters */
const bonusQuestion = computed(() => {
    const examGuid = currentExam.value?.examGuid
    if (!examGuid) {
        return null
    }

    const examGuidQuestionLibString = import.meta.env.VUE_APP_BONUS_QUESTION_EXPERIMENT_EXAM_GUID_QUESTION_LIB
    const examGuidQuestionLib = JSON.parse(examGuidQuestionLibString || '{}') as {
        [examGuid: string]: {
            prompt: string
            answer: string
            explanation: string
            reference: string
        }
    }
    return examGuidQuestionLib[examGuid]
})

const isBonusQuestionExperimentActive = computed(() => {
    // Check if current date is within experiment date range
    const currentDate = new Date()
    const dateRangeString = import.meta.env.VUE_APP_BONUS_QUESTION_EXPERIMENT_DATE_RANGE
    const dateRange = JSON.parse(dateRangeString || '[]') as [string, string]
    const [ startDate, endDate ] = dateRange.map(dateString => new Date(dateString))
    if (currentDate < startDate || currentDate > endDate) {
        return false
    }

    // Check if we have a current exam
    if (!currentExam.value?.examGuid) {
        return false
    }

    // Check if we have a bonus experiment question for the current exam
    if (!bonusQuestion.value) {
        return false
    }

    return true
})

const showBonusQuestion = computed(() => {
    if (disableExperiment.value) {
        return false
    }
    if (!bonusQuestion.value) {
        return false
    }

    return bonusQuestionVariantValue.value === 'short-answer-question'
})

const showUserExplanationField = computed(() => {
    if (disableExperiment.value) {
        return false
    }

    return bonusQuestionVariantValue.value === 'explanation-answer-question'
})

const bonusQuestionUserText = computed(() => {
    return bonusQuestionVariantValue.value === 'short-answer-question'
        ? userShortAnswer.value
        : userExplanation.value
})
/* End Bonus Question Experiment Getters */

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
        quizModule.actions.fetchAnsweredQuestions(),
        qotdModule.actions.fetchCurrentQotDMetric(),
        qotdModule.actions.fetchCurrentQotDQuestion(),
        userModule.actions.fetchUserData(),
    ])

    if (quizId.value && !question.value) {
        quizModule.actions.fetchAnsweredQuestions(quizId.value)
    }

    currentExam.value = examMetadataModule.getters.getCurrentExamMetadata()

    if (question.value && currentExam.value) {
        globalMetrics.value = await qotdModule.actions.fetchQotDMetric({
            examGuid: currentExam.value.examGuid,
            serial: question.value.serial,
        })

        await keywordsModule.actions.fetchKeywordDefinitions([ question.value.serial ])
    }

    // Setup Bonus Question Experiment
    if (isBonusQuestionExperimentActive.value) {
        const variant = await analyticsModule.actions.amplitudeFetchVariant('bonus-question-experiment')
        const variantValue = variant?.value as 'short-answer-question' | 'explanation-answer-question'
        bonusQuestionVariantValue.value = variantValue || null
        initializeBonusQuestionExperiment()
    }

    const questionSerial = question.value?.serial
    isFlagged.value = !!(questionSerial && flaggedQuestions.value.includes(questionSerial))

    isLoading.value = false
})

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
}

const keywordClicked = (params?: {
    keyword: string
    location: string
    clickLocation: { x: number; y: number }
    target: HTMLElement
}) => {
    showKeywordDefinition.value = false
    if (!params?.keyword) {
        return
    }

    const questionKeywords = question.value
        && keywordsModule.getters.getKeywordDefinitions().get(question.value.serial)
    const definition = questionKeywords?.get(params.keyword.toLowerCase())

    if (!definition) {
        return
    }

    analyticsModule.actions.amplitudeTrack('Keyword Definition Viewed', {
        examGuid: currentExam.value?.examGuid,
        definitionKeyword: params.keyword,
        serial: question.value?.serial,
        location: params.location,
        quizMode: -1,
        studyMode: route.params.quizId ? 'Review' : 'Active Quiz',
    })

    visibleKeywordDefinition.value = {
        keyword: params.keyword,
        definition,
        location: params.location,
    }
    keywordDOMElement.value = params.target
    keywordClickLocation.value = params.clickLocation

    nextTick(() => {
        showKeywordDefinition.value = true
    })
}

const toggleGlobalMetrics = (newVal: boolean) => {
    showGlobalMetrics.value = newVal
}

const toggleFlag = async () => {
    // Prevent if already processing toggleQuestionFlag call
    if (isTogglingFlag.value) {
        return
    }

    isTogglingFlag.value = true
    isFlagged.value = !isFlagged.value
    const serial = question.value?.serial

    try {
        await userExamMetadataModule.actions.toggleQuestionFlag(serial)
    } catch (e) {
        // noop
    } finally {
        isFlagged.value = !!(serial && flaggedQuestions.value.includes(serial))
        isTogglingFlag.value = false
    }
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    userModule.actions.updateQuizSettings({
        enableKeyboardShortcuts: newVal,
    })
}

const submitQotD = async (answerParam: Study.Cloud.IQuizAnswer) => {
    // Prevent duplicate submission
    if (isSubmitLoading.value) {
        return
    }
    isSubmitLoading.value = true
    if (answerParam.isCorrect) {
        isCorrect.value = answerParam.isCorrect
    }
    // Store the bonus question experiment answer before submitting the quiz
    if (showUserExplanationField.value) {
        const examGuid = currentExam.value?.examGuid
        localStorage.setItem(`bonusQuestionExperiment_${examGuid}_explanationAnswer`, userExplanation.value)
        analyticsModule.actions.amplitudeTrack('Bonus Question Answered', {
            bonusQuestionText: userExplanation.value,
            bonusQuestionVariation: 'explanationAnswerQuestion',
        })
    }
    await qotdModule.actions.recordQotD(answerParam)
    isSubmitLoading.value = false

    // Show the bonus question experiment survey 3 seconds after submitting the quiz
    if (showUserExplanationField.value) {
        setTimeout(() => {
            showSurvey.value = true
        }, 3000)
    }
}

const quitQuiz = () => {
    const fromNameVal = fromName.value
    if (fromNameVal && fromNameVal !== 'shared-qotd' && fromNameVal !== 'quiz-result') {
        router.back()
    } else {
        router.push({ name: 'study' })
    }
}

/* Start Bonus Question Experiment Methods */
const selectChoices = (answerParam: Study.Cloud.IQuizAnswer) => {
    selectedChoices.value = answerParam.selectedChoices
}

const initializeBonusQuestionExperiment = () => {
    const isBonusQuestionComplete = localStorage.getItem('bonusQuestionExperimentComplete')
    if (bonusQuestionVariantValue.value === 'explanation-answer-question') {
        const examGuid = currentExam.value?.examGuid
        const localStorageAnswer = localStorage.getItem(`bonusQuestionExperiment_${examGuid}_explanationAnswer`)
        if (localStorageAnswer) {
            userExplanation.value = localStorageAnswer
            disableExperiment.value = false  // Enable to show user their previous answer
        } else if (localStorageAnswer === null && answer.value) {
            disableExperiment.value = true   // Disable if qotd answered outside of experiment
        } else if (isBonusQuestionComplete) {
            disableExperiment.value = true   // Disable if already participated in experiment
        } else {
            disableExperiment.value = false  // Enable if qotd unanswered and experiment still incomplete
        }
    } else if (bonusQuestionVariantValue.value === 'short-answer-question') {
        const examGuid = currentExam.value?.examGuid
        const localStorageAnswer = localStorage.getItem(`bonusQuestionExperiment_${examGuid}_shortAnswer`)
        if (localStorageAnswer === null && answer.value) {
            disableExperiment.value = true   // Disable if qotd answered outside of experiment
        } else if (isBonusQuestionComplete) {
            disableExperiment.value = true   // Disable if already participated in experiment
        } else {
            disableExperiment.value = false  // Enable if qotd unanswered and experiment still incomplete
        }
    }
}

const skipBonusQuestion = () => {
    localStorage.setItem('bonusQuestionExperimentComplete', 'true')
    disableExperiment.value = true
    isCommunityStatsDisabled.value = !answer.value
}

const updateUserShortAnswer = (userAnswer: string) => {
    userShortAnswer.value = userAnswer
}

const submitSurvey = () => {
    showSurvey.value = false
    toastModule.actions.displayToast({
        title: 'Thank you for your feedback!',
    })
    completeBonusQuestionExperiment()
}

const closeSurvey = () => {
    showSurvey.value = false
    completeBonusQuestionExperiment()
}

const completeBonusQuestionExperiment = () => {
    localStorage.setItem('bonusQuestionExperimentComplete', 'true')
    if (bonusQuestionVariantValue.value === 'short-answer-question') {
        disableExperiment.value = true
    }
    isCommunityStatsDisabled.value = !answer.value
}
/* End Bonus Question Experiment Methods */

const explanationViewToggled = (showExplanation: boolean) => {
    if (showExplanation) {
        analyticsModule.actions.amplitudeTrack(
            'Explanation Opened',
            {
                examGuid: currentExam.value?.examGuid,
                bundleId: bundle.value?.objectId,
                serial: question.value?.serial,
                quizMode: -1,
                quizPlatform: 'Web',
                isSharedQotd: false,
                isReviewMode: false,
            }
        )
    }
}
</script>

<style lang="scss" scoped>
.qotd {
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;

    :deep(.uikit-quiz-container__main) {
        @include breakpoint(black-bear) {
            height: calc(100% - 55px);
        }
    }

    :deep(.uikit-quiz-container__question--mobile) {
        @include breakpoint(black-bear) {
            overflow: auto;
        }
    }

    :deep(.uikit-quiz-container__header--mobile) {
        @include breakpoint(black-bear) {
            height: 1px;
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: center;

        @include breakpoint(black-bear) {
            justify-content: space-around;
            margin: 0 auto;
            width: 375px;
        }
    }

    &__footer-left {
        display: flex;
        align-items: center;
    }

    &__global-metrics-toggle {
        margin-right: 24px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;

        @include breakpoint(black-bear) {
            margin-right: 0;
        }

        &--active {
            background: $black-feather;
        }
    }

    &__share {
        &--mobile {
            display: none;

            @include breakpoint(black-bear) {
                display: block;
            }
        }

        &--desktop {
            display: block;

            @include breakpoint(black-bear) {
                display: none;
            }
        }
    }

    &__exam-info {
        position: absolute;
        left: -10px;
        width: 320px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__quit-btn {
        position: absolute;
        right: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__question {
        height: 100%;

        &--correct{
            :deep(.uikit-question-choices-container__choice-container--correct) {
                &::after {
                    content: '';
                    border: 2px solid $cadaverous;
                    animation: fade-it .75s ease-in-out;

                    @media (prefers-reduced-motion: reduce) {
                        animation: none;
                    }

                    @keyframes fade-it {
                        0%  { box-shadow: 0px 0px 2px 2px rgba($cadaverous, 0.8); }
                        25%  { box-shadow: 0px 0px 4px 3px rgba($cadaverous, 0.6); }
                        50%  { box-shadow: 0px 0px 6px 5px rgba($cadaverous, 0.5); }
                        75%  { box-shadow: 0px 0px 8px 7px rgba($cadaverous, 0.4); }
                        90%  { box-shadow: 0px 0px 10px 8px rgba($cadaverous, 0.3); }
                        100%  { box-shadow: 0px 0px 11px 10px rgba($cadaverous, 0.2); }
                    }
                }
            }
        }
    }

    &__controls {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);

        @include breakpoint(black-bear) {
            position: relative;
            left: auto;
            transform: initial;
        }
    }

    &__flag-toggle {
        margin: 0 80px;

        @include breakpoint(black-bear) {
            margin: 0;
        }
    }

    &__keyboard-shortcuts-btn {
        position: absolute;
        right: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }
}
</style>
